import * as React from 'react';
import { styled } from 'styled-components';

import { TextButton } from '@creditornot/cb-button';
import { CaretRight, CaretUp } from '@creditornot/cb-icons';
import { typographyCss } from '@creditornot/cb-ingredients';
import { color } from '@creditornot/cb-ingredients/design-tokens';

import Skeleton from 'common/components/Skeleton';
import { MIDNIGHT_BLUE_TOKEN } from 'common/constants';

import { useCurrentCompany } from './hooks';
import ListOfCompany from './ListOfCompany';

const CompanyName = styled.div`
  ${typographyCss.caption}
  grid-area: name;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
  display: block;
  text-align: left;
`;

const CompanyDescription = styled.div`
  ${typographyCss.footnote}
  grid-area: address;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
  text-align: left;
`;

const IconContainer = styled.div`
  fill: currentColor;
  grid-area: icon;
  width: 12px;
  height: 12px;
`;

const StyledCompanySelectorButton = styled(TextButton)`
  display: grid;
  grid-template-columns: 1fr auto;
  grid-template-areas:
    'name icon'
    'address address';
  justify-items: start;
  width: 100%;
  border-radius: 8px;
  padding: 8px;
  color: ${color.fixedWhite};

  svg {
    fill: ${color.fixedWhite} !important;
  }

  &:hover {
    color: ${color.fixedWhite} !important;
    background: ${MIDNIGHT_BLUE_TOKEN.COLOR.HOVER};
  }
`;

const Root = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const StyledListOfCompany = styled(ListOfCompany)`
  overflow: scroll;
  max-height: 55vh;
`;

const Divider = styled.div`
  border-radius: var(--borderRadius);
  width: 100%;
  height: 1px;
  background: ${MIDNIGHT_BLUE_TOKEN.COLOR.ACTIVE};
`;

const CompanySelector = () => {
  const [isCompaniesSectionCollapsed, setIsCompaniesSectionCollapsed] =
    React.useState<boolean>(true);
  const { currentCompany, companiesByUser } = useCurrentCompany();

  const handleCompanySelectorOnClick = () => {
    setIsCompaniesSectionCollapsed(!isCompaniesSectionCollapsed);
  };
  // We won't be able to fetch the company information when there is none of company assigned to the user.
  const isPending = companiesByUser.data?.length === 0 ? false : currentCompany.isPending;

  return (
    <Root>
      {!isCompaniesSectionCollapsed ? (
        <>
          <StyledListOfCompany />
          <Divider />
        </>
      ) : null}

      <StyledCompanySelectorButton
        icon={
          <IconContainer>
            {isCompaniesSectionCollapsed ? <CaretRight /> : <CaretUp />}
          </IconContainer>
        }
        iconPosition="end"
        wrap
        disabled={isPending || companiesByUser.data?.length === 0}
        onClick={handleCompanySelectorOnClick}
      >
        <CompanyName title={currentCompany.data?.name}>
          {isPending ? (
            <Skeleton $width="8rem" $height="calc(1.25rem - 2px - 2px)" />
          ) : (
            (currentCompany.data?.name ?? '<none>')
          )}
        </CompanyName>
        <CompanyDescription title={currentCompany.data?.description}>
          {isPending ? (
            <Skeleton $width="12rem" $height="height: calc(1rem - 2px - 2px)" />
          ) : (
            (currentCompany.data?.description ?? null)
          )}
        </CompanyDescription>
      </StyledCompanySelectorButton>
    </Root>
  );
};

export default CompanySelector;
